import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import "../themeupdate/themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import {
  generateLayout,
  generateLayoutOptions,
  getWordStartPositions,
} from "../../../functions/crosswordLayout";
import { getUsableCrosswordQuestions, toLayoutWords } from "../../../functions/crosswordQuestions";
import { isThemeFlagOn } from "../../../pages/gameSetupStorage";

const CLUE_TYPE_TAG = { image: "[Image] ", video: "[Video] " };

const GridPattern = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  // Same filtering as the game (getCrosswordPairs) so this preview matches the real grid.
  const pairs = useMemo(
    () => getUsableCrosswordQuestions(data?.questions_and_answers),
    [data]
  );

  // "Shuffle Grid" (admin Rules) on → the same grid options the game picks from;
  // off → just the one grid every player gets.
  const gridShuffle = isThemeFlagOn(data?.grid_shuffle, false);
  const layouts = useMemo(() => {
    if (pairs.length < 2) return [];
    const words = toLayoutWords(pairs);
    return gridShuffle ? generateLayoutOptions(words) : [generateLayout(words)];
  }, [pairs, gridShuffle]);

  const [optionIndex, setOptionIndex] = useState(0);
  useEffect(() => {
    if (optionIndex >= layouts.length) setOptionIndex(0);
  }, [layouts.length, optionIndex]);
  const layout = layouts[optionIndex] || layouts[0] || null;

  const wordStartPositions = useMemo(
    () => (layout ? getWordStartPositions(layout.result) : {}),
    [layout]
  );

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  const across = layout ? layout.result.filter((w) => w.orientation === "across") : [];
  const down = layout ? layout.result.filter((w) => w.orientation === "down") : [];

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/themeupdate")}
        >
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-panel-wide">
        <div className="themeupdate-card crossword-preview-card">
          <div className="themeupdate-card-header">This is the Grid view of the puzzle generated</div>
          <div className="themeupdate-card-body">
            {!layout ? (
              <p className="crossword-preview-empty">
                Add at least 2 question/answer pairs on the Question Settings page to generate a grid.
              </p>
            ) : (
              <div className="crossword-preview-wrap">
                {gridShuffle ? (
                  <>
                    <p className="crossword-options-note">
                      Shuffle Grid is on — each player randomly gets one of these{" "}
                      {layouts.length} grid{layouts.length === 1 ? "" : "s"}.
                    </p>
                    {layouts.length > 1 ? (
                      <div className="crossword-options-tabs" role="tablist">
                        {layouts.map((_, i) => (
                          <button
                            key={`grid-option-${i}`}
                            type="button"
                            role="tab"
                            aria-selected={i === optionIndex}
                            className={`crossword-options-tab${i === optionIndex ? " is-active" : ""}`}
                            onClick={() => setOptionIndex(i)}
                          >
                            Grid {i + 1}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
                <div
                  className="crossword-grid"
                  style={{ gridTemplateColumns: `repeat(${layout.cols}, 40px)` }}
                >
                  {layout.table.map((row, i) =>
                    row.map((cell, j) => {
                      const isBlock = cell === "-";
                      const posKey = `${i + 1},${j + 1}`;
                      const number = wordStartPositions[posKey];
                      return (
                        <div
                          key={`${i}-${j}`}
                          className={`crossword-cell${isBlock ? " crossword-cell--block" : ""}`}
                        >
                          {number ? <span className="crossword-cell-number">{number}</span> : null}
                          {!isBlock ? (
                            <span className="crossword-cell-letter">{cell}</span>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="crossword-clues">
                  <div className="crossword-clues-section">
                    <h4>Across</h4>
                    {across.length ? (
                      across
                        .sort((a, b) => wordStartPositions[`${a.starty},${a.startx}`] - wordStartPositions[`${b.starty},${b.startx}`])
                        .map((w) => (
                          <p className="crossword-clue" key={`across-${w.startx}-${w.starty}`}>
                            {wordStartPositions[`${w.starty},${w.startx}`]}. {CLUE_TYPE_TAG[w.type] || ""}{w.clue} ({w.answer})
                          </p>
                        ))
                    ) : (
                      <p className="crossword-clue">None</p>
                    )}
                  </div>
                  <div className="crossword-clues-section">
                    <h4>Down</h4>
                    {down.length ? (
                      down
                        .sort((a, b) => wordStartPositions[`${a.starty},${a.startx}`] - wordStartPositions[`${b.starty},${b.startx}`])
                        .map((w) => (
                          <p className="crossword-clue" key={`down-${w.startx}-${w.starty}`}>
                            {wordStartPositions[`${w.starty},${w.startx}`]}. {CLUE_TYPE_TAG[w.type] || ""}{w.clue} ({w.answer})
                          </p>
                        ))
                    ) : (
                      <p className="crossword-clue">None</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GridPattern;
